"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import { classNames } from "../../utilities/props.js";
import * as keys from "../../constants/keys.js";
import s from "./Actionable.module.css";
const Actionable = forwardRef((props, ref) => {
    const { children, href, onClick, type, disabled, insetFocus, disableFocusRing, borderRadius, as, stopPropagation, fullWidth, className, attributes, } = props;
    const rootClassNames = classNames(s.root, className, disabled && s["--disabled"], borderRadius && s[`--radius-${borderRadius}`], insetFocus && s["--inset"], disableFocusRing && s["--disabled-focus-ring"], fullWidth && s["--full-width"]);
    const rootAttributes = { ...attributes };
    const hasClickHandler = onClick || attributes?.onClick;
    const hasFocusHandler = attributes?.onFocus || attributes?.onBlur;
    const isLink = Boolean(href || attributes?.href);
    // Including attributes ref for the cases when event listeners are added through it
    // To make sure it doesn't render a span
    const isButton = Boolean(hasClickHandler || hasFocusHandler || type || attributes?.ref);
    const renderedAsButton = !isLink && isButton && (!as || as === "button");
    // Using any here to let TS save on type resolving, otherwise TS throws an error due to the type complexity
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let TagName;
    if (isLink) {
        TagName = "a";
        rootAttributes.href = disabled ? undefined : href || attributes?.href;
    }
    else if (renderedAsButton) {
        TagName = "button";
        rootAttributes.type = type || attributes?.type || "button";
        rootAttributes.disabled = disabled || attributes?.disabled;
    }
    else if (isButton) {
        const isFocusable = as === "label";
        const simulateButton = !isFocusable || hasClickHandler || hasFocusHandler;
        TagName = as || "span";
        rootAttributes.role = simulateButton ? "button" : undefined;
        rootAttributes.tabIndex = simulateButton ? 0 : undefined;
    }
    else {
        TagName = as || "span";
    }
    const handlePress = (event) => {
        if (disabled)
            return;
        if (stopPropagation)
            event.stopPropagation();
        onClick?.(event);
        attributes?.onClick?.(event);
    };
    const handleKeyDown = (event) => {
        const isSpace = event.key === keys.SPACE;
        const isEnter = event.key === keys.ENTER;
        if (!isSpace && !isEnter)
            return;
        if (rootAttributes.role !== "button")
            return;
        if (stopPropagation)
            event.stopPropagation();
        event.preventDefault();
        handlePress(event);
    };
    return (_jsx(TagName, { ref: ref, ...rootAttributes, "aria-disabled": disabled ? true : undefined, className: rootClassNames, onClick: handlePress, onKeyDown: handleKeyDown, children: children }));
});
Actionable.displayName = "Actionable";
export default Actionable;
